import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  FlightTakeoff,
  CheckCircle,
  Timeline,
  Assignment,
  AttachMoney,
  Explore,
  AccountBalance,
  EmojiEvents,
} from '@mui/icons-material';

const visaTypes = [
  {
    title: 'Standard Visitor Visa',
    description: 'For tourism, visiting family/friends, or business activities',
    duration: 'Up to 6 months',
    activities: [
      'Tourism and leisure',
      'Visiting family and friends',
      'Business meetings',
      'Academic conferences',
    ],
  },
  {
    title: 'Long-term Visitor Visa',
    description: 'For frequent visitors to the UK',
    duration: '2, 5, or 10 years',
    activities: [
      'Multiple visits',
      'Maximum 6 months per visit',
      'Business networking',
      'Tourism activities',
    ],
  },
  {
    title: 'Marriage Visitor Visa',
    description: 'For those getting married or forming a civil partnership in the UK',
    duration: 'Up to 6 months',
    activities: [
      'Getting married in the UK',
      'Giving notice of marriage',
      'Tourism after ceremony',
    ],
  },
  {
    title: 'Permitted Paid Engagement',
    description: 'For specific paid work in the UK',
    duration: 'Up to 1 month',
    activities: [
      'Guest speaking',
      'Examining students',
      'Participating in arts/entertainment',
      'Legal services',
    ],
  },
];

const requirements = [
  'Valid passport',
  'Proof of sufficient funds',
  'Travel itinerary',
  'Accommodation details',
  'Return travel booking',
  'Evidence of ties to home country',
];

const process = [
  {
    title: 'Eligibility Check',
    description: 'Assessment of your visitor visa category and requirements',
  },
  {
    title: 'Document Preparation',
    description: 'Guidance on collecting and organizing required documents',
  },
  {
    title: 'Application Support',
    description: 'Assistance with online application and forms',
  },
  {
    title: 'Document Submission',
    description: 'Support with biometrics and document submission',
  },
  {
    title: 'Travel Preparation',
    description: 'Guidance on UK entry and stay requirements',
  },
];

const VisitorVisa = () => {
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <FlightTakeoff sx={{ fontSize: 64, mb: 3, color: theme.palette.secondary.light }} />
              <Typography variant="h1" gutterBottom>
                UK Visitor Visa Services
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Your gateway to exploring the United Kingdom
              </Typography>
              <Button
                component={RouterLink}
                to="/consultation"
                variant="contained"
                color="secondary"
                size="large"
                sx={{ mr: 2 }}
              >
                Free Consultation
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Overview Section */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom>
              Visit the UK
            </Typography>
            <Typography variant="body1" paragraph>
              UK visitor visas allow you to enter the United Kingdom for tourism, business meetings, or special occasions. Our expert team provides comprehensive support to help you choose the right visitor visa category and ensure a successful application.
            </Typography>
            <Typography variant="body1">
              Whether you're planning a holiday, visiting family, attending business meetings, or getting married in the UK, we'll guide you through every step of the visa application process.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Key Requirements
                </Typography>
                <List>
                  {requirements.map((requirement) => (
                    <ListItem key={requirement}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary={requirement} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Visa Types Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Types of Visitor Visas
          </Typography>
          <Grid container spacing={4}>
            {visaTypes.map((visa) => (
              <Grid item xs={12} md={6} key={visa.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                      {visa.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {visa.description}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Duration: {visa.duration}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      Permitted Activities:
                    </Typography>
                    <List>
                      {visa.activities.map((activity) => (
                        <ListItem key={activity}>
                          <ListItemIcon>
                            <Assignment color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={activity} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Application Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Application Process
          </Typography>
          <Grid container spacing={3}>
            {process.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Timeline color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Step {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" gutterBottom>
            Why Choose Us
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmojiEvents color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Expert Guidance
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    Our team of experienced immigration lawyers specializes in UK visitor visas. We stay updated with the latest immigration rules and policies to provide you with accurate advice and support.
                  </Typography>
                  <Typography variant="body1">
                    We have helped thousands of visitors successfully obtain their UK visas and enjoy their stay in the United Kingdom.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Explore color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">
                      Comprehensive Support
                    </Typography>
                  </Box>
                  <Typography variant="body1" paragraph>
                    We provide end-to-end support throughout your visa application process, from initial assessment to travel preparation.
                  </Typography>
                  <Typography variant="body1">
                    Our services include application review, document checking, and guidance on UK entry requirements to ensure a smooth journey.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            py: 8,
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: 3,
          }}
        >
          <Typography variant="h3" gutterBottom>
            Ready to Visit the UK?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
            Contact us today for a free consultation and expert guidance
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="secondary"
            size="large"
            sx={{ px: 6, py: 2 }}
          >
            Book Free Consultation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default VisitorVisa; 